import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Row, Col, Spinner, Alert, Form } from 'react-bootstrap';
import { FiArrowLeft, FiUser, FiMail, FiPhone, FiMapPin, FiBriefcase, FiSave } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { createAdminAccount } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import { ADMIN_USER_ROLES, DEFAULT_ADMIN_COMPANY_NAME } from '../../constants/app';
import RoleDetailsButton from '../../components/Admin/RoleDetailsButton';

const AdminUserCreate = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        company_name: DEFAULT_ADMIN_COMPANY_NAME,
        country_code: '',
        role: '',
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Réinitialiser les erreurs lors de la modification
        if (error) setError(null);
        if (success) setSuccess(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError(null);
            setSuccess(null);

            // Préparer les données pour l'API (enlever les champs vides optionnels)
            const submitData = {
                email: formData.email.trim(),
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim(),
                role: formData.role,
            };

            // Ajouter les champs optionnels seulement s'ils ne sont pas vides
            if (formData.phone_number.trim()) submitData.phone_number = formData.phone_number.trim();
            if (formData.company_name.trim()) submitData.company_name = formData.company_name.trim();
            if (formData.country_code.trim()) submitData.country_code = formData.country_code.trim();

            await createAdminAccount(submitData);
            setSuccess(t('pages.adminUserCreate.success'));
            
            // Rediriger vers la liste après 1.5 secondes
            setTimeout(() => {
                navigate('/admin/management');
            }, 1500);
        } catch (err) {
            setError(handleApiError(err, t('pages.adminUserCreate.error')));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="dashboard-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="page-title">
                                <FiUser className="me-2" />
                                {t('pages.adminUserCreate.title')}
                            </h2>
                            <p className="text-muted mb-0">{t('pages.adminUserCreate.subtitle')}</p>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            <RoleDetailsButton selectedRole={formData.role} />
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/management')}>
                                <FiArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <Alert variant="danger" className="mb-4">
                            <strong>{t('common.error')} :</strong> {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert variant="success" className="mb-4">
                            <strong>{t('common.success')} :</strong> {success}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">{t('pages.adminUserCreate.mainInfo')}</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                {t('pages.adminUserCreate.email')} <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                                placeholder={t('pages.adminUserCreate.placeholder.email')}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                {t('pages.adminUserCreate.phone')}
                                            </Form.Label>
                                            <Form.Control
                                                type="tel"
                                                name="phone_number"
                                                value={formData.phone_number}
                                                onChange={handleInputChange}
                                                placeholder={t('pages.adminUserCreate.placeholder.phone')}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Prénom <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="first_name"
                                                value={formData.first_name}
                                                onChange={handleInputChange}
                                                required
                                                minLength={2}
                                                maxLength={100}
                                                placeholder="Prénom"
                                            />
                                            <Form.Text className="text-muted">
                                                Entre 2 et 100 caractères
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Nom <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="last_name"
                                                value={formData.last_name}
                                                onChange={handleInputChange}
                                                required
                                                minLength={2}
                                                maxLength={100}
                                                placeholder="Nom"
                                            />
                                            <Form.Text className="text-muted">
                                                Entre 2 et 100 caractères
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Entreprise</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="company_name"
                                                value={formData.company_name}
                                                onChange={handleInputChange}
                                                placeholder="Nom de l'entreprise"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Code pays</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="country_code"
                                                value={formData.country_code}
                                                onChange={handleInputChange}
                                                placeholder="Ex: +33"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Rôle <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="role"
                                                value={formData.role}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Sélectionner un rôle</option>
                                                {ADMIN_USER_ROLES.map(({ value, label }) => (
                                                    <option key={value} value={value}>{label}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <div className="d-flex justify-content-end gap-2">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/admin/management')}
                                disabled={loading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button 
                                variant="primary" 
                                type="submit" 
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        {t('pages.adminUserCreate.creating')}
                                    </>
                                ) : (
                                    <>
                                        <FiSave className="me-2" />
                                        {t('pages.adminUserCreate.createButton')}
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminUserCreate;
